import { useQuery } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { can, useMe } from '../../auth.tsx';
import { Modal } from '../../components/Modal.tsx';
import { Button } from '../../components/ui.tsx';
import { ImportFlow, type ImportKindCard } from './Imports.tsx';

const LINK = 'text-sm text-brand-text underline-offset-2 hover:underline';

/**
 * Importar de dentro da tela (ADR 0040, adendo de 28/10/2026): o mesmo motor e o mesmo template
 * oficial da Central de Importações, numa janela por cima da lista. Só aparece para quem pode
 * validar aquele cadastro (a mesma regra da Central); nada é gravado antes da confirmação expressa.
 * Ao confirmar, as consultas são atualizadas e a lista da tela já mostra o resultado.
 */
export function ImportButton({
  kind,
  named = false,
}: {
  kind: string;
  /** Mostra o nome do cadastro no botão, quando a tela tem mais de uma importação. */
  named?: boolean;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const kinds = useQuery({
    queryKey: ['masterdata-imports', 'kinds'],
    queryFn: () =>
      api.get<{ items: ImportKindCard[]; confirmMinutes: number; maxFileBytes: number }>(
        '/masterdata-imports/kinds',
      ),
    enabled: can(me, 'masterdata.import.read'),
  });
  const card = kinds.data?.items.find((k) => k.id === kind);
  if (!kinds.data || !card?.actions.validate) return null;
  const templateUrl = (mode: 'empty' | 'update') =>
    `/api/masterdata-imports/kinds/${kind}/template?mode=${mode}`;
  return (
    <>
      <Button variant="secondary" title={t('imports.inScreen.hint')} onClick={() => setOpen(true)}>
        <Upload size={16} aria-hidden />
        {named
          ? t('imports.inScreen.buttonNamed', { name: t(`imports.kinds.${kind}.title`) })
          : t('imports.inScreen.button')}
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="xl"
        title={t('imports.wizardTitle', { name: t(`imports.kinds.${kind}.title`) })}
        description={t('imports.inScreen.description', { minutes: kinds.data.confirmMinutes })}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <a className={LINK} href={templateUrl('empty')}>
              {t('imports.downloadTemplate')}
            </a>
            {card.actions.export && (
              <a className={LINK} href={templateUrl('update')}>
                {t('imports.downloadExisting')}
              </a>
            )}
            <a className={LINK} href={`/masterdata/imports/history?kind=${kind}`}>
              {t('imports.history')}
            </a>
          </div>
          <ImportFlow
            kind={card}
            maxBytes={kinds.data.maxFileBytes}
            onClose={() => setOpen(false)}
          />
        </div>
      </Modal>
    </>
  );
}
